import { Button, Container, Element, Label, TextAreaInput, TextInput } from '@playcanvas/pcui';

import { Pose } from '../camera-poses';
import { Events } from '../events';
import { initAnnotationReorder } from './annotation-reorder';
import { ColorPanel } from './color-panel';
import { i18n } from './localization';
import { SplatList } from './splat-list';
import cameraPanelSvg from './svg/camera.svg';
import sceneImportSvg from './svg/import.svg';
import sceneNewSvg from './svg/new.svg';
import soloSvg from './svg/solo.svg';
import { Tooltips } from './tooltips';
import { Transform } from './transform';

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

class ScenePanel extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'scene-panel',
            class: 'panel'
        };

        super(args);

        // stop pointer events bubbling
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const sceneHeader = new Container({
            class: 'panel-header'
        });

        const sceneIcon = new Label({
            text: '\uE344',
            class: 'panel-header-icon'
        });

        const sceneLabel = new Label({
            class: 'panel-header-label'
        });
        i18n.bindText(sceneLabel, 'panel.scene');

        let soloActive = false;

        const soloToggle = new Container({
            class: 'panel-header-button'
        });
        soloToggle.dom.appendChild(createSvg(soloSvg));

        soloToggle.on('click', () => {
            soloActive = !soloActive;
            if (soloActive) {
                soloToggle.class.add('active');
            } else {
                soloToggle.class.remove('active');
            }
            events.fire('scene.solo', soloActive);
        });

        const sceneImport = new Container({
            class: 'panel-header-button'
        });
        sceneImport.dom.appendChild(createSvg(sceneImportSvg));

        const sceneNew = new Container({
            class: 'panel-header-button'
        });
        sceneNew.dom.appendChild(createSvg(sceneNewSvg));

        sceneHeader.append(sceneIcon);
        sceneHeader.append(sceneLabel);
        sceneHeader.append(soloToggle);
        sceneHeader.append(sceneImport);
        sceneHeader.append(sceneNew);

        sceneImport.on('click', async () => {
            await events.invoke('scene.import');
        });

        sceneNew.on('click', () => {
            events.invoke('doc.new');
        });

        tooltips.register(soloToggle, () => i18n.t('tooltip.scene.solo'), 'top');
        tooltips.register(sceneImport, () => i18n.t('tooltip.scene.import'), 'top');
        tooltips.register(sceneNew, () => i18n.t('tooltip.scene.new'), 'top');

        const splatList = new SplatList(events);

        const splatListContainer = new Container({
            class: 'splat-list-container'
        });
        splatListContainer.append(splatList);

        // The scene tree is deliberately separate from the Scene Manager
        // header. World-space overlays are shown as children of the 3DGS
        // content root, matching the runtime entity hierarchy used for
        // normalization and future overlay families.
        const sceneTree = new Container({ class: 'scene-tree' });
        const sceneTreeRoot = new Container({ class: ['scene-tree-root', 'panel-header'] });
        const sceneTreeIcon = new Label({
            text: '\uE111',
            class: 'panel-header-icon'
        });
        const sceneTreeLabel = new Label({
            class: 'panel-header-label'
        });
        sceneTreeLabel.text = '3DGS';
        const sceneTreeChildren = new Container({ class: 'scene-tree-children' });
        sceneTreeRoot.append(sceneTreeIcon);
        sceneTreeRoot.append(sceneTreeLabel);
        sceneTree.append(sceneTreeRoot);
        sceneTreeChildren.append(splatListContainer);

        const cameraSection = new Container({ class: 'scene-tree-section' });
        const cameraHeader = new Container({ class: ['panel-header', 'camera-list-header'] });
        const cameraHeaderIcon = new Container({ class: 'panel-header-icon' });
        cameraHeaderIcon.dom.appendChild(createSvg(cameraPanelSvg));
        const cameraHeaderLabel = new Label({ class: 'panel-header-label' });
        i18n.bindText(cameraHeaderLabel, 'panel.scene.cameras');
        const cameraImport = new Button({
            class: ['panel-header-button', 'camera-import-button']
        });
        cameraImport.text = 'Import';
        const cameraClear = new Button({
            class: ['panel-header-button', 'camera-clear-button'],
            hidden: true
        });
        cameraClear.text = 'Clear';
        const cameraImportHint = new Label({
            class: 'camera-import-hint',
            text: '?'
        });
        cameraHeader.append(cameraHeaderIcon);
        cameraHeader.append(cameraHeaderLabel);
        cameraHeader.append(cameraImport);
        cameraHeader.append(cameraClear);
        cameraHeader.append(cameraImportHint);

        cameraImport.on('click', async (event: Event) => {
            event.stopPropagation();
            await events.invoke('scene.importCameras');
        });
        cameraClear.on('click', (event: Event) => {
            event.stopPropagation();
            events.fire('camera.clearPoses');
        });
        cameraImportHint.on('click', (event: Event) => event.stopPropagation());
        tooltips.register(cameraImportHint, () => i18n.t('tooltip.scene.import-cameras'), 'top');
        tooltips.register(cameraClear, () => i18n.t('tooltip.scene.clear-cameras'), 'top');

        const cameraList = new Container({ class: 'camera-list' });
        const cameraItems: Array<Button & { pose?: Pose }> = [];
        let cameraListExpanded = true;
        const updateCameraListVisibility = (poses: readonly Pose[] = events.invoke('camera.poses') ?? []) => {
            const hasPoses = poses.length > 0;
            cameraList.hidden = !hasPoses || !cameraListExpanded;
            cameraClear.hidden = !hasPoses;
            cameraHeader.class[cameraListExpanded ? 'remove' : 'add']('collapsed');
            if (!hasPoses) cameraHeader.class.remove('collapsed');
        };
        cameraHeader.on('click', () => {
            cameraListExpanded = !cameraListExpanded;
            updateCameraListVisibility();
        });
        const updateCameraList = (poses: readonly Pose[] = events.invoke('camera.poses') ?? []) => {
            // Keep PCUI buttons attached and reuse them. Removing a PCUI
            // element from the DOM triggers its destroy lifecycle, which can
            // race observer unregister handlers when cameras are cleared.
            cameraItems.forEach((item) => {
                item.hidden = true;
            });
            updateCameraListVisibility(poses);
            poses.forEach((pose, index) => {
                let item = cameraItems[index];
                if (!item) {
                    item = new Button({
                        class: 'camera-list-item'
                    });
                    item.on('click', () => {
                        const currentPose = item.pose;
                        if (!currentPose) return;
                        const transformed = events.invoke('camera.transformPose', currentPose);
                        events.fire('camera.setPose', {
                            position: transformed.position,
                            target: transformed.target,
                            fov: currentPose.fov
                        });
                    });
                    cameraItems.push(item);
                    cameraList.append(item);
                }
                item.pose = pose;
                item.text = pose.label ?? pose.name ?? `Camera ${index + 1}`;
                item.hidden = false;
            });
        };
        events.on('camera.posesChanged', updateCameraList);
        updateCameraList();

        const transformHeader = new Container({
            class: ['panel-header', 'transform-header']
        });

        const transformIcon = new Label({
            text: '\uE111',
            class: 'panel-header-icon'
        });

        const transformLabel = new Label({
            class: 'panel-header-label'
        });
        i18n.bindText(transformLabel, 'panel.scene.transform');

        transformHeader.append(transformIcon);
        transformHeader.append(transformLabel);

        const transform = new Transform(events);
        let transformExpanded = true;
        transformHeader.dom.addEventListener('click', () => {
            transformExpanded = !transformExpanded;
            transform.dom.style.display = transformExpanded ? '' : 'none';
            transformHeader.class[transformExpanded ? 'remove' : 'add']('collapsed');
        });

        cameraSection.append(cameraHeader);
        cameraSection.append(cameraList);
        sceneTreeChildren.append(cameraSection);

        const annotationSection = new Container({ class: 'scene-tree-section' });
        const annotationHeader = new Container({ class: ['panel-header', 'annotation-list-header'] });
        const annotationHeaderIcon = new Container({ class: ['panel-header-icon', 'annotation-header-icon'] });
        annotationHeaderIcon.class.add('annotation-note-icon');
        const annotationHeaderLabel = new Label({ class: 'panel-header-label', text: 'Annotations' });
        annotationHeader.append(annotationHeaderIcon);
        annotationHeader.append(annotationHeaderLabel);

        const annotationClear = new Button({
            class: ['panel-header-button', 'annotation-clear-button'],
            hidden: true
        });
        annotationClear.text = 'Clear';
        annotationHeader.append(annotationClear);

        const annotationList = new Container({ class: 'annotation-list' });
        const annotationItems: Button[] = [];
        const annotationRows: Container[] = [];
        const annotationMoveUp: Button[] = [];
        const annotationMoveDown: Button[] = [];
        const cancelAnnotationDrag = initAnnotationReorder(
            annotationList.dom,
            () => annotationRows.filter(row => !row.hidden).map(row => row.dom),
            (from, to) => events.fire('annotation.move', from, to)
        );
        this.on('destroy', cancelAnnotationDrag);
        let annotationListExpanded = true;
        const updateAnnotationList = (annotations: readonly { title: string }[] = events.invokeOptional('annotations') ?? []) => {
            cancelAnnotationDrag();
            annotationItems.forEach((item) => {
                item.hidden = true;
            });
            annotationRows.forEach((row) => {
                row.hidden = true;
            });
            annotationClear.hidden = annotations.length === 0;
            annotationList.hidden = annotations.length === 0 || !annotationListExpanded;
            annotationHeader.class[annotationListExpanded ? 'remove' : 'add']('collapsed');
            annotations.forEach((annotation, index) => {
                let item = annotationItems[index];
                if (!item) {
                    item = new Button({ class: 'annotation-list-item' });
                    item.on('click', () => {
                        if (events.invoke('annotation.selected') === index) events.fire('annotation.deselect');
                        else events.fire('annotation.select', index);
                    });
                    annotationItems.push(item);
                    const row = new Container({ class: 'annotation-list-row' });
                    const move = new Container({ class: 'annotation-list-move' });
                    const moveUp = new Button({ class: 'annotation-list-move-up', text: '▲' });
                    const moveDown = new Button({ class: 'annotation-list-move-down', text: '▼' });
                    const dragHandle = new Container({ class: 'annotation-list-drag-handle' });
                    dragHandle.dom.textContent = '☰';
                    dragHandle.dom.title = 'Drag to reorder annotation';
                    moveUp.dom.title = 'Move annotation up';
                    moveDown.dom.title = 'Move annotation down';
                    moveUp.dom.setAttribute('aria-label', 'Move annotation up');
                    moveDown.dom.setAttribute('aria-label', 'Move annotation down');
                    moveUp.on('click', (event: Event) => {
                        event.stopPropagation();
                        events.fire('annotation.move', index, index - 1);
                    });
                    moveDown.on('click', (event: Event) => {
                        event.stopPropagation();
                        events.fire('annotation.move', index, index + 1);
                    });
                    move.append(moveUp);
                    move.append(moveDown);
                    annotationMoveUp.push(moveUp);
                    annotationMoveDown.push(moveDown);
                    const remove = new Button({ class: 'annotation-list-delete', text: '×' });
                    remove.on('click', (event: Event) => {
                        event.stopPropagation();
                        events.fire('annotation.delete', index);
                    });
                    row.append(move);
                    row.append(dragHandle);
                    row.append(item);
                    row.append(remove);
                    annotationRows.push(row);
                    annotationList.append(row);
                }
                item.text = annotation.title || `Annotation ${index + 1}`;
                item.class[events.invokeOptional('annotation.selected') === index ? 'add' : 'remove']('active');
                annotationMoveUp[index].disabled = index === 0;
                annotationMoveDown[index].disabled = index === annotations.length - 1;
                item.hidden = false;
                annotationRows[index].hidden = false;
            });
        };
        annotationClear.on('click', (event: Event) => {
            event.stopPropagation();
            events.fire('annotation.clear');
        });
        events.on('annotations.changed', updateAnnotationList);
        updateAnnotationList();
        annotationSection.append(annotationHeader);
        annotationSection.append(annotationList);

        const annotationEditor = new Container({ class: 'annotation-editor', hidden: true });
        const annotationTitle = new TextInput({ class: 'annotation-editor-input', placeholder: 'Label' });
        const annotationText = new TextAreaInput({ class: 'annotation-editor-text', placeholder: 'Text' });
        annotationEditor.append(annotationTitle);
        annotationEditor.append(annotationText);
        const annotationMoveHint = new Label({
            class: 'annotation-camera-hint',
            text: 'Drag an axis to move the annotation. Rotate the camera to adjust your view.'
        });
        annotationEditor.append(annotationMoveHint);
        const annotationDone = new Button({ text: 'Done editing' });
        annotationDone.on('click', () => {
            events.invoke('annotation.commitEdits');
            events.fire('annotation.deselect');
        });
        const annotationCameraHint = new Label({
            class: 'annotation-camera-hint',
            text: 'Adjust the camera, then save.',
            hidden: true
        });
        annotationCameraHint.dom.setAttribute('role', 'status');
        const annotationCameraActions = new Container({ class: 'annotation-camera-actions' });
        const annotationUpdateCamera = new Button({ class: 'annotation-update-camera', text: 'Edit camera view' });
        const annotationCameraIcon = createSvg(cameraPanelSvg);
        annotationUpdateCamera.dom.prepend(annotationCameraIcon);
        const annotationCancelCamera = new Button({ class: 'annotation-cancel-camera', text: 'Cancel', hidden: true });
        annotationCameraActions.append(annotationUpdateCamera);
        annotationCameraActions.append(annotationCancelCamera);
        annotationEditor.append(annotationCameraHint);
        annotationEditor.append(annotationCameraActions);
        annotationEditor.append(annotationDone);
        events.on('annotation.cameraEditingChanged', (editing: boolean) => {
            annotationUpdateCamera.text = editing ? 'Save camera view' : 'Edit camera view';
            annotationUpdateCamera.dom.prepend(annotationCameraIcon);
            annotationCancelCamera.hidden = !editing;
            annotationCameraHint.hidden = !editing;
            annotationMoveHint.hidden = editing;
            annotationEditor.dom.classList.toggle('editing-camera', editing);
        });
        annotationCancelCamera.on('click', () => events.fire('annotation.cancelCamera'));
        annotationUpdateCamera.on('click', (event: Event) => {
            event.stopPropagation();
            events.fire(events.invoke('annotation.editingCamera') ? 'annotation.updateCamera' : 'annotation.editCamera');
        });
        annotationTitle.on('change', (value: string) => {
            const index = events.invoke('annotation.selected');
            if (index >= 0) events.fire('annotation.update', index, { title: value });
        });
        annotationText.on('change', (value: string) => {
            const index = events.invoke('annotation.selected');
            if (index >= 0) events.fire('annotation.update', index, { text: value });
        });
        // PCUI commits text edits on change/blur. Commit the live field values
        // before creating another annotation so a canvas click cannot discard
        // the text of the currently selected annotation.
        events.function('annotation.commitEdits', () => {
            const index = events.invoke('annotation.selected');
            if (index < 0) return;
            events.fire('annotation.update', index, {
                title: annotationTitle.value,
                text: annotationText.value
            });
        });
        const updateAnnotationEditor = (annotations: readonly { title: string, text: string }[] = events.invokeOptional('annotations') ?? []) => {
            const index = events.invokeOptional('annotation.selected') ?? -1;
            const annotation = annotations[index];
            const wasHidden = annotationEditor.hidden;
            annotationEditor.hidden = !annotation || !annotationListExpanded;
            if (annotation) {
                annotationRows[index]?.dom.after(annotationEditor.dom);
                annotationTitle.value = annotation.title;
                annotationText.value = annotation.text;
                if (wasHidden) {
                    annotationEditor.class.add('opening');
                    requestAnimationFrame(() => annotationEditor.class.remove('opening'));
                }
            } else {
                annotationList.dom.after(annotationEditor.dom);
            }
        };
        annotationHeader.on('click', () => {
            events.fire('annotation.cancelCamera');
            annotationListExpanded = !annotationListExpanded;
            updateAnnotationList();
            if (!annotationListExpanded) {
                annotationEditor.hidden = true;
            } else {
                updateAnnotationEditor();
            }
        });
        events.on('annotations.changed', updateAnnotationEditor);
        updateAnnotationEditor();
        annotationSection.append(annotationEditor);
        sceneTreeChildren.append(annotationSection);

        let sceneTreeExpanded = true;
        const updateSceneTreeVisibility = () => {
            sceneTreeChildren.hidden = !sceneTreeExpanded;
            sceneTreeRoot.class[sceneTreeExpanded ? 'remove' : 'add']('collapsed');
        };
        sceneTreeRoot.on('click', () => {
            events.fire('annotation.cancelCamera');
            sceneTreeExpanded = !sceneTreeExpanded;
            updateSceneTreeVisibility();
        });
        updateSceneTreeVisibility();
        sceneTree.append(sceneTreeChildren);

        this.append(sceneHeader);
        this.append(sceneTree);
        this.append(transformHeader);
        this.append(transform);
        this.append(new ColorPanel(events));
        this.append(new Element({
            class: 'panel-header',
            height: 20
        }));
    }
}

export { ScenePanel };
